/**
 * Showings setup (BR-112): the one question "Are you generally available to show it?", then the panels
 * for the answer. The answer is saved on the Listing (`showingsAnswer`, BR-107) the moment it is picked
 * (or with "Continue" when it was only arrow-keyed), so it survives a reload; changing it never deletes
 * saved times. Open houses can be added under both answers. Used by the guide's Showings step and by the
 * live listing's Showings section.
 *
 * Every time, window, exception and open house saves as soon as it is submitted; booking preferences
 * save with the step's "Save and continue" (guide) or their own button (live listing). `handleRef`
 * lets the guide ask whether anything is unsaved and flush it.
 */

import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent, type Ref } from 'react';
import { CalendarDays, Clock, Info } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { todayInTimezone } from '@resida/shared';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import { updateListing, type Listing } from '../../../../lib/leasing.api';
import { cn } from '../../../../lib/utils';
import type { GuideStepHandle } from '../../listing-guide/guide-steps';
import { availabilityQueryOptions, openHousesQueryOptions } from './availability.query';
import { BookingPreferences } from './BookingPreferences';
import { ExceptionsPanel } from './ExceptionsPanel';
import { OpenHousesPanel } from './OpenHousesPanel';
import { InlineError, LINK_BUTTON_CLASS, type PanelReport } from './panel-support';
import { impliedShowingsAnswer, type ShowingsMode } from './showings-mode';
import { SpecificTimesPanel } from './SpecificTimesPanel';
import { WeeklyHoursPanel } from './WeeklyHoursPanel';

type PanelKey = 'weekly' | 'exceptions' | 'specific' | 'openHouses' | 'booking';
const PANEL_KEYS: readonly PanelKey[] = ['weekly', 'exceptions', 'specific', 'openHouses', 'booking'];
type FlagKind = Parameters<PanelReport>[0];
type Flags = Record<FlagKind, Partial<Record<PanelKey, boolean>>>;

const ANSWERS: { mode: ShowingsMode; title: string; description: string; Icon: typeof Clock }[] = [
  {
    mode: 'general',
    title: 'Yes, most days',
    description: 'I will set my usual weekly hours, then block out the days I am away.',
    Icon: Clock,
  },
  {
    mode: 'limited',
    title: 'No, only at set times',
    description: 'I will pick the days I can do private showings, host open houses, or both.',
    Icon: CalendarDays,
  },
];

const PANEL_COPY: Record<ShowingsMode, { badge: string; title: string; description: string }> = {
  general: {
    badge: 'Mostly available',
    title: 'Set your weekly hours',
    description: 'Prospects can request any time inside these hours. Block out days you are away in the next section.',
  },
  limited: {
    badge: 'Only at set times',
    title: 'Set the times you can show it',
    description: 'Add private showing times, open houses, or both. You need at least one.',
  },
};

interface ShowingsSetupProps {
  listing: Listing;
  /** True inside the guide: booking preferences save with "Save and continue". */
  embedded?: boolean;
  /** The heading level of the question (1 in the guide, where it is the page title). */
  headingLevel?: 1 | 2;
  handleRef?: Ref<GuideStepHandle>;
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
  /** Guide only: told "Continue" while the question is on screen (nothing there can be saved), else null. */
  onPrimaryLabelChange?: (label: string | null) => void;
}

export function ShowingsSetup({
  listing,
  embedded = false,
  headingLevel = 2,
  handleRef,
  onDirtyChange,
  onSavingChange,
  onPrimaryLabelChange,
}: ShowingsSetupProps) {
  const isClosed = listing.status === 'closed';
  const queryClient = useQueryClient();
  const availability = useQuery(availabilityQueryOptions(listing.id));
  const openHouses = useQuery(openHousesQueryOptions(listing.id));

  const [flags, setFlags] = useState<Flags>({ saving: {}, open: {}, dirty: {} });
  const setFlag = useCallback((kind: FlagKind, key: PanelKey, value: boolean) => {
    setFlags((prev) => (!!prev[kind][key] === value ? prev : { ...prev, [kind]: { ...prev[kind], [key]: value } }));
  }, []);
  const reporters = useMemo(
    () =>
      Object.fromEntries(
        PANEL_KEYS.map((key) => [key, ((kind, value) => setFlag(kind, key, value)) as PanelReport])
      ) as Record<PanelKey, PanelReport>,
    [setFlag]
  );
  const anyFlag = (kind: FlagKind) => Object.values(flags[kind]).some(Boolean);
  const formOpen = anyFlag('open');

  // The saved answer (BR-107) is the source of truth; `picked` is the landlord's pick on screen until
  // the listing refetch brings the saved answer up to date. Nothing is ever deleted by switching.
  const savedAnswer: ShowingsMode | null = listing.showingsAnswer;
  const [picked, setPicked] = useState<ShowingsMode | null>(null);
  const [choosing, setChoosing] = useState<boolean | null>(null);
  const [answerSaving, setAnswerSaving] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  // A live listing (active, paused, closed) with saved times but no saved answer is a row from before the
  // answer was stored: show the answer its times imply, so those times stay visible (a closed listing
  // cannot be answered at all). A draft with no answer stays on the unanswered question (BR-107).
  const impliedAnswer: ShowingsMode | null =
    listing.status !== 'draft' && savedAnswer === null && availability.data && openHouses.data
      ? impliedShowingsAnswer(availability.data.rules, availability.data.exceptions, openHouses.data.items)
      : null;
  const mode = picked ?? savedAnswer ?? impliedAnswer;
  const showChoice = choosing ?? mode === null;
  const saving = anyFlag('saving') || answerSaving;
  const answerUnsaved = !isClosed && picked !== null && picked !== savedAnswer;
  const dirty = formOpen || anyFlag('dirty') || answerUnsaved;

  useEffect(() => {
    if (picked !== null && picked === savedAnswer) setPicked(null);
  }, [picked, savedAnswer]);

  const persistAnswer = useCallback(
    async (answer: ShowingsMode): Promise<boolean> => {
      if (answer === listing.showingsAnswer) return true;
      setAnswerSaving(true);
      setAnswerError(null);
      try {
        await updateListing(listing.id, { showingsAnswer: answer });
        await queryClient.invalidateQueries({ queryKey: ['listing', listing.id] });
        return true;
      } catch (err) {
        setAnswerError(err instanceof Error ? err.message : 'Failed to save your answer');
        return false;
      } finally {
        setAnswerSaving(false);
      }
    },
    [listing.id, listing.showingsAnswer, queryClient]
  );

  const answerRefs = useRef<Record<ShowingsMode, HTMLButtonElement | null>>({ general: null, limited: null });
  const bookingRef = useRef<GuideStepHandle>(null);
  const [openFormNote, setOpenFormNote] = useState(false);
  const stateRef = useRef({ dirty, formOpen, picked, persistAnswer });
  stateRef.current = { dirty, formOpen, picked, persistAnswer };

  useImperativeHandle(
    handleRef,
    () => ({
      isDirty: () => stateRef.current.dirty,
      save: async () => {
        if (stateRef.current.formOpen) {
          setOpenFormNote(true);
          return false;
        }
        setOpenFormNote(false);
        // An answer that was only arrow-keyed is saved here, so "Continue" never loses it.
        const { picked: unsavedPick, persistAnswer: saveAnswer } = stateRef.current;
        if (unsavedPick && !(await saveAnswer(unsavedPick))) return false;
        return (await bookingRef.current?.save()) ?? true;
      },
    }),
    []
  );

  useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);
  useEffect(() => onSavingChange?.(saving), [saving, onSavingChange]);
  useEffect(() => {
    if (!formOpen) setOpenFormNote(false);
  }, [formOpen]);

  const loaded = !availability.isLoading && !openHouses.isLoading && !!availability.data && !!openHouses.data;
  const questionOnScreen = loaded && (showChoice || !mode);
  useEffect(() => {
    onPrimaryLabelChange?.(questionOnScreen ? 'Continue' : null);
    return () => onPrimaryLabelChange?.(null);
  }, [questionOnScreen, onPrimaryLabelChange]);

  // ARIA radio pattern: one tab stop (the checked answer, else the first), arrows move focus and check.
  // Checking by arrow keeps the question on screen; click, Enter or Space opens the answer's panels.
  const onAnswerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step || isClosed) return;
    event.preventDefault();
    const current = Math.max(0, ANSWERS.findIndex((answer) => answer.mode === mode));
    const next = ANSWERS[(current + step + ANSWERS.length) % ANSWERS.length]!.mode;
    setPicked(next);
    setChoosing(true);
    answerRefs.current[next]?.focus();
  };

  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const headingClass = 'text-xl font-semibold text-foreground';

  if (availability.isLoading || openHouses.isLoading) {
    return (
      <div data-testid="showings-setup-loading" className="space-y-4" aria-busy="true">
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (availability.error || openHouses.error || !availability.data || !openHouses.data) {
    const failure = availability.error ?? openHouses.error;
    return (
      <div data-testid="availability-error" role="alert" className="space-y-3 rounded-md border border-destructive/30 bg-destructive/10 p-4">
        <p className="text-sm text-destructive">
          {failure instanceof Error ? failure.message : 'Failed to load showing times.'}
        </p>
        <Button
          type="button"
          variant="outline"
          data-testid="availability-retry"
          onClick={() => {
            void availability.refetch();
            void openHouses.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const { rules, exceptions } = availability.data;
  const { items: openHouseItems, timezone } = openHouses.data;
  const today = todayInTimezone(timezone);

  return (
    <div data-testid="showings-setup" className="space-y-5">
      {showChoice || !mode ? (
        <div className="space-y-4">
          <div>
            <Heading className={headingClass} data-testid="showings-question" id="showings-question-heading">
              Are you generally available to show it?
            </Heading>
            <p className="mt-1 text-sm text-muted-foreground">
              Pick the closer fit. We will show only the settings that matter for it, and you can change your answer
              later without losing what you set.
            </p>
          </div>
          <div
            role="radiogroup"
            aria-labelledby="showings-question-heading"
            className="space-y-3"
            onKeyDown={onAnswerKeyDown}
          >
            {ANSWERS.map(({ mode: answer, title, description, Icon }, answerIndex) => {
              const checked = mode === answer;
              return (
                <button
                  key={answer}
                  ref={(node) => {
                    answerRefs.current[answer] = node;
                  }}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  tabIndex={checked || (!mode && answerIndex === 0) ? 0 : -1}
                  data-testid={`showings-answer-${answer}`}
                  disabled={isClosed}
                  onClick={() => {
                    setPicked(answer);
                    setChoosing(false);
                    void persistAnswer(answer).then((saved) => {
                      if (saved) return;
                      // Not saved: fall back to the saved answer and keep the question on screen.
                      setPicked(null);
                      setChoosing(true);
                    });
                  }}
                  className={cn(
                    'flex min-h-[44px] w-full items-center gap-4 rounded-lg border p-4 text-left transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
                    checked ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted/50'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-md',
                      checked ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block text-base font-semibold text-foreground">{title}</span>
                    <span className="block text-sm text-muted-foreground">{description}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <p
            data-testid="showings-open-house-note"
            className="flex items-center gap-2 rounded-md border border-info/25 bg-info/10 p-3 text-sm text-info"
          >
            <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
            Either way, you can add open houses on top.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="primary" data-testid="showings-answer-badge">
              {PANEL_COPY[mode].badge}
            </Badge>
            <button
              type="button"
              data-testid="showings-change-answer"
              disabled={isClosed || formOpen}
              onClick={() => setChoosing(true)}
              className={LINK_BUTTON_CLASS}
            >
              Change
            </button>
          </div>
          <Heading className={headingClass} id="showings-question-heading" data-testid="showings-heading">
            {PANEL_COPY[mode].title}
          </Heading>
          <p className="text-sm text-muted-foreground">{PANEL_COPY[mode].description}</p>
          {mode === 'limited' && rules.length > 0 && (
            <p data-testid="showings-weekly-kept-note" className="text-sm text-muted-foreground">
              Your weekly hours are still saved and still apply. Change your answer to edit them.
            </p>
          )}
        </div>
      )}

      {!showChoice && mode && (
        <>
          {mode === 'general' ? (
            <>
              <WeeklyHoursPanel listingId={listing.id} isClosed={isClosed} rules={rules} report={reporters.weekly} />
              <ExceptionsPanel
                listingId={listing.id}
                isClosed={isClosed}
                exceptions={exceptions}
                today={today}
                report={reporters.exceptions}
              />
            </>
          ) : (
            <SpecificTimesPanel
              listingId={listing.id}
              isClosed={isClosed}
              exceptions={exceptions}
              today={today}
              report={reporters.specific}
            />
          )}
          <OpenHousesPanel
            listingId={listing.id}
            isClosed={isClosed}
            openHouses={openHouseItems}
            timezone={timezone}
            today={today}
            optional={mode === 'general'}
            report={reporters.openHouses}
          />
          <BookingPreferences
            listing={listing}
            isClosed={isClosed}
            embedded={embedded}
            handleRef={bookingRef}
            report={reporters.booking}
          />
        </>
      )}

      {answerError && <InlineError testId="showings-answer-error" message={answerError} />}

      {openFormNote && (
        <InlineError
          testId="showings-open-form-note"
          message="Finish or cancel the form you have open, then continue."
        />
      )}
    </div>
  );
}
